import { useEffect, useState } from "react"
import { useRouter } from "next/router"
import axios from "axios"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import styled from "styled-components"
import { useTranslation } from "next-i18next"
import { useController, useForm } from "react-hook-form"
import Link from "next/link"
import { ErrorMessage } from "@hookform/error-message"
import { Select } from "@/common/components/_UI"
import { zodResolver } from "@hookform/resolvers/zod"
import { countryList } from "../countries"
import { theme } from "@/common/Mixin"
import * as z from "zod"
import { SolutionNames, SolutionPlans } from "@/common/data/Products"
import RadioButtonGroup, { RadioButtonGroupOption } from "./RadioButtonGroup"
import Button from "../_UI/ButtonNew"

interface Option {
  label: string
  value: string
}

const contactWayList: Option[] = [
  {
    label: "Telegram",
    value: "Telegram"
  },
  {
    label: "Line",
    value: "Line"
  },
  {
    label: "WhatsApp",
    value: "WhatsApp"
  },
  {
    label: "Others",
    value: "Others"
  }
]

function asTuple<T extends string>(arr: T[]): [T, ...T[]] {
  if (arr.length === 0) {
    throw new Error("Array cannot be empty")
  }
  return arr as [T, ...T[]]
}

interface ContactUsFormProps {
  plans?: SolutionPlans[]
  style?: React.CSSProperties
  page: SolutionNames | "contact-us" | "asset-pro"
  maxHeight?: string
}

export default function ContactUsForm({
  plans,
  style,
  page,
  maxHeight
}: ContactUsFormProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const { t } = useTranslation(["contactUs"])
  const howToHelpOptions = [
    t("form.howToHelp.newOption1"),
    t("form.howToHelp.newOption2"),
    t("form.howToHelp.newOption3"),
    t("form.howToHelp.newOption4"),
    t("form.howToHelp.newOption5"),
    t("form.howToHelp.newOption6")
  ]
  const howManyUsersOptions = [
    { text: t("form.howManyUsers.option1"), value: "Less Than 5000" },
    { text: t("form.howManyUsers.option2"), value: "More Than 5000" }
  ]

  const planOption: Record<SolutionPlans, RadioButtonGroupOption> = {
    [SolutionPlans.GameFiPro]: {
      label: t("form.planOption.gameFiPro.label"),
      value: SolutionPlans.GameFiPro,
      description: t("form.planOption.gameFiPro.desc")
    },
    [SolutionPlans.GameFiLite]: {
      label: t("form.planOption.gameFiLite.label"),
      value: SolutionPlans.GameFiLite,
      description: t("form.planOption.gameFiLite.desc")
    },
    [SolutionPlans.DefiPro]: {
      label: t("form.planOption.deFiPro.label"),
      value: SolutionPlans.DefiPro,
      description: t("form.planOption.deFiPro.desc")
    },
    [SolutionPlans.DefiLite]: {
      label: t("form.planOption.deFiLite.label"),
      value: SolutionPlans.DefiLite,
      description: t("form.planOption.deFiLite.desc")
    },
    [SolutionPlans.Web3FundManagement]: {
      label: t("form.planOption.Web3FundManagement.label"),
      value: SolutionPlans.Web3FundManagement,
      description: t("form.planOption.Web3FundManagement.desc")
    },
    [SolutionPlans.Other]: {
      label: t("form.planOption.other.label"),
      value: SolutionPlans.Other,
      description: t("form.planOption.other.desc")
    }
  }
  const phoneRegex = new RegExp(
    /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
  )
  const formSchema = z.object({
    page: z
      .enum([
        ...asTuple(Object.values(SolutionNames)),
        "contact-us",
        "asset-pro"
      ])
      .default(page),
    solutionPlan: z
      .enum(asTuple(Object.values(SolutionPlans)))
      .optional()
      .nullable(),
    name: z.string().min(1, { message: t("form.name.required") }),
    company: z.string().min(1, { message: t("form.company.required") }),
    title: z.string().min(1, { message: t("form.title.required") }),
    country: z.string({ required_error: t("form.country.required") }).min(1),
    email: z
      .string()
      .min(1, { message: t("form.email.required") })
      .email({ message: t("form.email.format.error") }),
    phoneNumber: z
      .string()
      .min(1, { message: t("form.phone.required") })
      .regex(phoneRegex, t("form.phone.format.error")),
    howToContact: z.enum(asTuple(contactWayList.map(x => x.value))).optional(),
    contactDetail: z.string().optional(),
    ...(["contact-us", "asset-pro", "web3-fund-management"].includes(page)
      ? {
          howToHelp: z
            .array(z.string(), {
              invalid_type_error: t("form.howToHelp.required")
            })
            .min(1)
        }
      : {}),
    howManyUsers: z.enum(["Less Than 5000", "More Than 5000"], {
      invalid_type_error: t("form.howManyUsers.required")
    }),
    whatYouNeed: z.string().optional(),
    agreeToTerms: z.boolean().refine(val => val === true, {
      message: t("form.agreeToTerms.required")
    })
  })

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm({ resolver: zodResolver(formSchema) })
  const {
    field: {
      value: solutionPlanValue,
      onChange: solutionPlanOnChange,
      ...restSolutionPlanField
    }
  } = useController({ name: "solutionPlan", control })
  const {
    field: {
      value: countryValue,
      onChange: countryOnChange,
      ...restCountryField
    }
  } = useController({ name: "country", control })
  const {
    field: {
      value: howToContactValue,
      onChange: howToContactOnChange,
      ...restHowToContactField
    }
  } = useController({ name: "howToContact", control })

  const router = useRouter()
  const { referrer } = router.query
  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async (data: any) => {
    data = { ...data, from: referrer === "undefined" ? "" : referrer }
    try {
      setIsLoading(true)
      // I know this api naming is weird but it's automatically generated by strapi
      await axios.post(`${process.env.NEXT_PUBLIC_CMS_HOST}/api/contact-uses`, {
        data
      })

      await axios.post("/api/new-contact-us", data)
      reset()
      setIsLoading(false)
      setModalOpen(true)
    } catch (err) {
      window.alert("Something went wrong. Please try again.")
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const { plan } = router.query
    if (plan) {
      reset({ solutionPlan: plan })
    }
  }, [reset, router.query])
  return (
    <>
      <FormWrapper
        id="contact-us-form"
        style={style}
        className="contactBox"
        onSubmit={handleSubmit(onSubmit)}
      >
        {plans && plans.length !== 0 && (
          <RadioButtonGroup
            options={plans.map(plan => planOption[plan])}
            register={register}
            value={solutionPlanValue}
            onChange={(option: string) => {
              if (option === solutionPlanValue) {
                solutionPlanOnChange(null)
              } else {
                solutionPlanOnChange(option)
              }
            }}
            {...restSolutionPlanField}
          />
        )}
        <FormContentWrapper
          style={{
            maxHeight,
            overflow: "auto"
          }}
        >
          <label htmlFor="name">
            <p>
              {t("form.name.label")}
              <RequireSymbol className="required">*</RequireSymbol>
            </p>
            <input
              {...register("name")}
              placeholder={t("form.name.placeholder")}
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => (
                <ErrorMessageWrapper className="body2">
                  {message}
                </ErrorMessageWrapper>
              )}
            />
          </label>

          <FormInputWrapper
            style={{
              width: "100%",
              gap: "32px"
            }}
          >
            <Col1Wrapper htmlFor="company">
              <p>
                {t("form.company.label")}
                <RequireSymbol className="required">*</RequireSymbol>
              </p>
              <input
                {...register("company")}
                placeholder={t("form.company.placeholder")}
              />
              <ErrorMessage
                errors={errors}
                name="company"
                render={({ message }) => (
                  <ErrorMessageWrapper className="body2">
                    {message}
                  </ErrorMessageWrapper>
                )}
              />
            </Col1Wrapper>

            <Col2Wrapper htmlFor="title">
              <p>
                {t("form.title.label")}
                <RequireSymbol className="required">*</RequireSymbol>
              </p>
              <input
                {...register("title")}
                placeholder={t("form.title.placeholder")}
              />
              <ErrorMessage
                errors={errors}
                name="title"
                render={({ message }) => (
                  <ErrorMessageWrapper className="body2">
                    {message}
                  </ErrorMessageWrapper>
                )}
              />
            </Col2Wrapper>
          </FormInputWrapper>

          <label htmlFor="country">
            <p>
              {t("form.country.label")}
              <RequireSymbol className="required">*</RequireSymbol>
            </p>
            <div style={{ width: "100%", textAlign: "start" }}>
              <Select
                options={countryList}
                value={
                  countryValue
                    ? countryList.find(x => x.value === countryValue)
                    : countryValue
                }
                onChange={(option: unknown) => {
                  countryOnChange((option as Option)?.label ?? option)
                }}
                {...restCountryField}
              />
              <ErrorMessage
                errors={errors}
                name="country"
                render={({ message }) => (
                  <ErrorMessageWrapper className="body2">
                    {message}
                  </ErrorMessageWrapper>
                )}
              />
            </div>
          </label>

          <FormInputWrapper
            style={{
              width: "100%",
              gap: "32px"
            }}
          >
            <Col1Wrapper htmlFor="email">
              <p>
                {t("form.email.label")}
                <RequireSymbol className="required">*</RequireSymbol>
              </p>
              <input
                placeholder={t("form.email.placeholder")}
                {...register("email")}
              />
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => (
                  <ErrorMessageWrapper className="body2">
                    {message}
                  </ErrorMessageWrapper>
                )}
              />
            </Col1Wrapper>
            <Col2Wrapper htmlFor="phoneNumber">
              <p>
                {t("form.phone.label")}
                <RequireSymbol className="required">*</RequireSymbol>
              </p>
              <input
                {...register("phoneNumber")}
                placeholder={t("form.phone.placeholder")}
              />
              <ErrorMessage
                errors={errors}
                name="phoneNumber"
                render={({ message }) => (
                  <ErrorMessageWrapper className="body2">
                    {message}
                  </ErrorMessageWrapper>
                )}
              />
            </Col2Wrapper>
          </FormInputWrapper>

          <label htmlFor="contact">
            <p>{t("form.contact.label")}</p>
            <FormInputWrapper
              style={{
                width: "100%"
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "auto",
                  textAlign: "start"
                }}
              >
                <Select
                  isSearchable={false}
                  options={contactWayList}
                  value={
                    howToContactValue
                      ? contactWayList.find(x => x.value === howToContactValue)
                      : howToContactValue
                  }
                  onChange={(option: unknown) =>
                    howToContactOnChange((option as Option)?.value ?? option)
                  }
                  {...restHowToContactField}
                />
                <ErrorMessage errors={errors} name="howToContact" />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%"
                }}
              >
                <input
                  placeholder={t("form.contact.placeholder")}
                  {...register("contactDetail")}
                />
                <ErrorMessage
                  errors={errors}
                  name="contactDetail"
                  render={({ message }) => (
                    <ErrorMessageWrapper className="body2">
                      {message}
                    </ErrorMessageWrapper>
                  )}
                />
              </div>
            </FormInputWrapper>
          </label>

          {["contact-us", "asset-pro", "web3-fund-management"].includes(
            page
          ) && (
            <label style={{ gap: "12px" }} htmlFor="howToHelp">
              <p>
                {t("form.howToHelp.label")}
                <RequireSymbol className="required">*</RequireSymbol>
              </p>
              <ErrorMessage
                errors={errors}
                name="howToHelp"
                render={({ message }) => (
                  <ErrorMessageWrapper className="body2">
                    {message}
                  </ErrorMessageWrapper>
                )}
              />
              {howToHelpOptions.map((option, idx) => (
                <SameLineWrapper key={option}>
                  <input
                    type="checkbox"
                    {...register(`howToHelp`)}
                    name="howToHelp"
                    id={option}
                    value={option}
                  />
                  <label htmlFor={option}>{option}</label>
                </SameLineWrapper>
              ))}
            </label>
          )}
          <label htmlFor="howManyUsers">
            <p>
              {t("form.howManyUsers.label")}
              <RequireSymbol className="required">*</RequireSymbol>
            </p>
            <ErrorMessage
              errors={errors}
              name="howManyUsers"
              render={({ message }) => (
                <ErrorMessageWrapper className="body2">
                  {message}
                </ErrorMessageWrapper>
              )}
            />
            <FormInputWrapper>
              {howManyUsersOptions.map((option, idx) => (
                <SameLineWrapper key={option.value}>
                  <input
                    type="radio"
                    {...register(`howManyUsers`)}
                    name="howManyUsers"
                    id={option.value}
                    value={option.value}
                  />
                  <label htmlFor={option.value}>{option.text}</label>
                </SameLineWrapper>
              ))}
            </FormInputWrapper>
          </label>

          <label htmlFor="whatYouNeed">
            <p>{t("form.whatYouNeed.label")}</p>
            <textarea
              style={{ width: "100%", resize: "vertical", border: "none" }}
              placeholder={t("form.whatYouNeed.placeholder")}
              {...register("whatYouNeed")}
              rows={5}
            />
          </label>
          <label className="CheckBoxWrap">
            <SameLineWrapper>
              <input type="checkbox" {...register("agreeToTerms")} />
              <span>
                {`${t("form.agreeToTerms.label")} `}
                <Link
                  style={{ color: "inherit", textDecoration: "underline" }}
                  href={"/company/legal"}
                  target="_blank"
                >
                  {`${t("form.agreeToTerms.link")}`}
                </Link>
              </span>
            </SameLineWrapper>
            <ErrorMessage
              errors={errors}
              name="agreeToTerms"
              render={({ message }) => (
                <ErrorMessageWrapper className="body2">
                  {message}
                </ErrorMessageWrapper>
              )}
            />
          </label>
        </FormContentWrapper>
        <Button
          loading={isLoading}
          type="submit"
          arrowType="none"
          content={t("form.submit")}
        />
      </FormWrapper>
      <Dialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
        }}
        aria-labelledby="form-submit-dialog-title"
        aria-describedby="form-submit-dialog-description"
      >
        <DialogTitleWrapper className="title">
          {t("complete.title")}
        </DialogTitleWrapper>
        <DialogContentWrapper>{t("complete.desc")}</DialogContentWrapper>
        <DialogActions sx={{ width: "100%" }}>
          <Button
            wide
            arrowType="none"
            content={"OK"}
            onClick={() => {
              setModalOpen(false)
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

const RequireSymbol = styled.span`
  padding-left: 4px;
`

const FormWrapper = styled.form`
  display: flex;
  padding: 3vh 2vw 3vh 3vw;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;

  border-radius: 20px;
  background: ${theme.color.textContrast};
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.1);
  @media (${theme.lg}) {
    padding: 5vh 5vw;
  }

  .error {
    border: 1px solid ${theme.color.error};
  }
`

const FormContentWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 0 3vw 0 1vw;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  label {
    p {
      color: ${theme.color.secondary};
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 150%; /* 24px */
      letter-spacing: 0.32px;
    }
    .required {
      color: ${theme.color.error};
    }
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;

    color: ${theme.color.textGray};
    text-align: left;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%; /* 25.6px */
    letter-spacing: 0.16px;
  }
  input:not([type="checkbox"]):not([type="radio"]) {
    background: ${theme.color.backgroundLight};
  }
  input::placeholder {
    color: ${theme.color.textLightGray};
  }
  textarea {
    background: ${theme.color.textContrast};
  }
  input[type="checkbox"] {
    accent-color: ${theme.color.checkBoxFill};
  }
  input[type="radio"] {
    accent-color: ${theme.color.checkBoxFill};
  }

  /* width */
  ::-webkit-scrollbar {
    width: 8px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: #ffffff;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${theme.color.textLightGray};
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`

const FormInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
  @media (${theme.lg}) {
    flex-direction: column;
  }
`

const SameLineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`

const Col1Wrapper = styled.label`
  width: 50%;
  @media (${theme.lg}) {
    width: 100%;
  }
`
const Col2Wrapper = styled.label`
  width: 45%;
  @media (${theme.lg}) {
    width: 100%;
  }
`

const ErrorMessageWrapper = styled.div`
  color: ${theme.color.error};
`

const DialogTitleWrapper = styled.span`
  color: ${theme.color.secondary};
`

const DialogContentWrapper = styled.span`
  color: ${theme.color.textGray};
`
